import React from 'react';
import DocumentPage, {
  Section,
  Paragraph,
  BulletList,
  Callout,
} from './DocumentPage';
import CalorVesselsNotice from './CalorVesselsNotice';

const ImpartialityConfidentialityDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Impartiality and Confidentiality Agreement"
    documentHeading="PVS Impartiality, Confidentiality & Social Media Agreement"
    includeDefaultMeta={false}
    meta={[
      ['Company', 'Pressure Vessel Services Limited'],
      ['Document Reference', 'PVS-POL-IMPCON-001'],
      ['Version', '1.0'],
      ['Date of Issue', '21st April 2026'],
      ['Next Review Date', '21st April 2028'],
      ['Review Period', '24 months'],
      ['Prepared by', 'Pressure Vessel Services Limited'],
      ['Approved by', 'Board of Directors'],
      ['Applicability', 'All PVS Directors, Inspectors, Engineers, Operatives and Contractors'],
    ]}
    acknowledgment={{
      documentName: 'Impartiality and Confidentiality Agreement',
      documentReference: 'PVS-POL-IMPCON-001',
      processActivity: 'Impartiality, Confidentiality & Social Media Conduct',
      versionNumber: '1.0',
      dateOfIssue: '21st April 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <CalorVesselsNotice />

    <Section number="1" title="Purpose and Scope">
      <Paragraph>
        Pressure Vessel Services Limited (PVS) operates as an independent inspection and
        refurbishment body. The integrity of the certificates, reports and records we issue, and
        the continued trust placed in us by our clients, depend entirely on the impartiality,
        objectivity and strict confidentiality with which every activity is carried out.
      </Paragraph>
      <Paragraph className="mt-2">
        This agreement applies to every PVS Director, employee, inspector, engineer, operative,
        subcontractor and authorised visitor with access to inspection data, client assets,
        premises or proprietary information. It forms part of the conditions of employment /
        engagement and must be read, signed and returned to a PVS Director prior to commencing or
        continuing inspection-related duties.
      </Paragraph>
    </Section>

    <Section number="2" title="Statement of Impartiality">
      <Paragraph>
        As an employee or representative of PVS, you are involved in or directly support
        independent inspection activities. To protect the integrity of our work, maintain client
        trust and demonstrate a consistently objective approach, all activities must be undertaken
        impartially. By signing this agreement I confirm that I will:
      </Paragraph>
      <BulletList
        items={[
          <><strong>Objectivity:</strong> Perform all inspection, refurbishment, testing and related activities objectively and solely on the basis of the factual evidence gathered, the applicable Written Scheme of Examination (WSE) and documented client requirements.</>,
          <><strong>Freedom from Undue Influence:</strong> Not allow commercial, financial, political, personal or any other pressure, whether internal or external, to compromise the quality, independence or outcome of my work.</>,
          <><strong>Conflict of Interest Declaration:</strong> Immediately disclose to a PVS Director any actual, potential or perceived conflict of interest. Examples include (but are not limited to): inspecting the work of a direct family member, a close personal or financial relationship with a client, supplier or installer, any financial interest in the outcome of an inspection, or recent prior employment with a client whose assets are being inspected.</>,
          <><strong>Consistency of Standard:</strong> Apply the same standard of inspection, acceptance criteria and documentation to every client and every vessel, regardless of the commercial size or ongoing value of the contract.</>,
          <><strong>Risk to Impartiality:</strong> Participate openly in the PVS periodic impartiality risk review and co-operate with any investigation into threats to impartiality arising from relationships, activities or finances.</>,
        ]}
      />
    </Section>

    <Section number="3" title="Confidentiality and Non-Disclosure">
      <Paragraph>
        Through the course of your employment or engagement, you will have access to proprietary
        information belonging to PVS and to our clients. PVS is responsible for the management and
        safeguarding of all information obtained or created during the performance of our
        activities, including inspection outcomes, technical data, commercial terms and personal
        data. By signing this agreement I confirm that I will:
      </Paragraph>
      <BulletList
        items={[
          <><strong>Proprietary Information:</strong> Keep all business operations, technical manuals, Written Schemes of Examination, inspection outcomes, test results, pricing, commercial terms and client data strictly confidential.</>,
          <><strong>Non-Disclosure:</strong> Not disclose any client or company information to unauthorised third parties (including friends, family, former colleagues, competitors or the press) without the explicit, written consent of a PVS Director.</>,
          <><strong>Data Security:</strong> Ensure that all digital records, credentials, passwords, tablets, laptops, USB media and physical documents are stored and transmitted securely and in accordance with PVS IT and data-protection procedures. Information shall only be accessed on a need-to-know basis.</>,
          <><strong>Release of Information:</strong> Recognise that, where PVS is legally required to release confidential information (for example by a regulator or court), the client concerned shall, unless prohibited by law, be notified by a PVS Director in advance of the disclosure.</>,
          <><strong>Post-Employment:</strong> Continue to honour the obligation of confidentiality after the termination of my employment or engagement, without time limit, in respect of all information acquired during my time with PVS.</>,
        ]}
      />
    </Section>

    <Section number="4" title="Social Media and Public Communications Policy">
      <Paragraph>
        To protect the integrity, impartiality and commercial reputation of PVS and our clients,
        strict rules apply regarding the use of social media (including but not limited to
        Facebook, X/Twitter, Instagram, TikTok, LinkedIn, YouTube, Reddit, Snapchat and any public
        or private WhatsApp / Telegram / messaging groups).
      </Paragraph>
      <BulletList
        items={[
          <><strong>Zero Tolerance for Disparagement:</strong> I will not post, share, repost, like or otherwise engage with any content that makes negative, disparaging or defamatory comments about PVS, our clients, our suppliers, our employees, or the assets we process.</>,
          <><strong>Protection of Client Assets:</strong> I will not take, store, or publish photographs, videos or written descriptions of client assets (including LPG vessels, serial numbers, data plates and site identifiers), client-branded vehicles, client-branded packaging, or PVS operational areas on any social media platform, even if my account is set to "private" or "friends only".</>,
          <><strong>Inspection Outcomes:</strong> I will never publicly discuss the outcome of any inspection, failure, rejection, vessel failure rate or specific client defect. Publicly discussing client failures is a direct breach of my impartiality and confidentiality obligations under this agreement.</>,
          <><strong>No Representation:</strong> I understand that unless explicitly authorised in writing by a Director, I do not speak on behalf of PVS or any of our clients. Any press, supplier or client enquiry shall be referred to a Director without comment.</>,
          <><strong>Use of PVS Systems:</strong> I will not transfer client photographs, inspection data or documents to personal devices, personal email accounts or personal cloud storage under any circumstances.</>,
        ]}
      />
      <Callout tone="warning">
        If in doubt, do not post. A member of the Management team will always assist in confirming
        what may or may not be shared externally.
      </Callout>
    </Section>

    <Section number="5" title="Reporting of Breaches and Threats to Impartiality">
      <Paragraph>
        Any individual who becomes aware of an actual or suspected breach of this agreement, or of a
        new threat to impartiality (for example a change in personal circumstances, a new financial
        interest, or external pressure on an inspection outcome), shall report this without delay to
        a PVS Director. Reports made in good faith will be treated confidentially and will not be
        grounds for any disadvantage to the reporter.
      </Paragraph>
    </Section>

    <Section number="6" title="Disciplinary Action">
      <Paragraph>
        Breaches of Impartiality, Confidentiality or the Social Media Policy are taken extremely
        seriously. Any violation of this agreement – particularly the posting of negative material
        regarding a client, or the unauthorised release of images or data relating to client assets
        or inspection outcomes – may be treated as <strong>gross misconduct</strong> and could
        result in immediate termination of employment / engagement, civil recovery of any loss
        suffered by PVS or its clients, and where appropriate, referral to the police or other
        enforcement authorities.
      </Paragraph>
    </Section>

    <Section number="7" title="Review">
      <Paragraph>
        This policy shall be reviewed by the Board of Directors at least every 24 months, or
        earlier following any significant change to PVS procedures, any significant change to
        client contractual requirements, or following any incident, complaint or breach that
        identifies a need for amendment.
      </Paragraph>
    </Section>

    <Section number="8" title="Declaration">
      <Paragraph>
        I have read, understood, and agree to abide by the PVS Impartiality, Confidentiality, and
        Social Media Agreement. I understand my obligations under this agreement and recognise
        that safeguarding client information and maintaining a professional, unbiased reputation is
        a fundamental condition of my employment / engagement with Pressure Vessel Services
        Limited.
      </Paragraph>
      <div className="mt-6 pvs-glass p-6 rounded-lg border border-pvs-accent/30 space-y-4 text-pvs-text-muted">
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          <span className="font-semibold w-64">Employee Name:</span>
          <span className="flex-1 border-b border-pvs-border h-6" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          <span className="font-semibold w-64">Job Title:</span>
          <span className="flex-1 border-b border-pvs-border h-6" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          <span className="font-semibold w-64">Employee Signature:</span>
          <span className="flex-1 border-b border-pvs-border h-6" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          <span className="font-semibold w-64">Date:</span>
          <span className="flex-1 border-b border-pvs-border h-6" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          <span className="font-semibold w-64">Counter-signed by PVS Director:</span>
          <span className="flex-1 border-b border-pvs-border h-6" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          <span className="font-semibold w-64">Date:</span>
          <span className="flex-1 border-b border-pvs-border h-6" />
        </div>
      </div>
    </Section>
  </DocumentPage>
);

export default ImpartialityConfidentialityDoc;
